/**
 * New lead — own tab (big form: identity, guardian, student level + conditional details,
 * interested courses, source, assignment, notes). Route: /crm/leads/new (tab key 'lead:new').
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabs, useTabLabel } from '../../components/shell/TabsContext';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, User, Student, Books, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { SOURCE_LABELS } from './CRMPage';

const EMPTY_LEAD = {
  first_name: '', last_name: '', email: '', phone: '', guardian_name: '',
  student_level: '', source: 'website', notes: '', assigned_to: '',
  interested_courses: [],
  school_class: '', school_name: '', school_board: '',
  college_year: '', college_name: '',
  professional_course: '', professional_level: '',
  competitive_exam: ''
};

export default function LeadForm() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { closeAndNavigate } = useTabs();
  useTabLabel('New lead');

  const formRef = useRef(null);
  const [newLead, setNewLead] = useState({ ...EMPTY_LEAD });
  const [courses, setCourses] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [coursesRes, staffRes] = await Promise.all([
          api.get('/courses').catch(() => ({ data: [] })),
          api.get('/users').catch(() => ({ data: [] }))
        ]);
        setCourses(coursesRes.data || []);
        setStaffMembers((staffRes.data || []).filter(u => ['instructor', 'tenant_admin'].includes(u.role)));
      } catch {}
    };
    load();
  }, [api]);

  const handleCreateLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/crm/leads', newLead);
      toast.success('Lead created');
      setNewLead({ ...EMPTY_LEAD });
      closeAndNavigate('lead:new', '/crm');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSubmitting(false);
    }
  };

  // Toolbar "Create lead" submits the <form> so native validation (required) still runs
  const submitForm = () => {
    const form = formRef.current;
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const toggleCourse = (id, checked) => {
    setNewLead((prev) => ({
      ...prev,
      interested_courses: checked
        ? [...prev.interested_courses, id]
        : prev.interested_courses.filter((c) => c !== id),
    }));
  };

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="lead-form"
        title="New lead"
        subtitle="Add a prospective student to the pipeline"
        onBack={() => navigate('/crm')}
       
        primary={[
          { key: 'save', label: submitting ? 'Creating…' : 'Create lead', icon: Plus, onClick: submitForm, disabled: submitting, testid: 'create-lead-btn' },
          { key: 'cancel', label: 'Cancel', variant: 'outline', onClick: () => navigate('/crm'), testid: 'cancel-lead-btn' },
        ]}
      >
        <form ref={formRef} onSubmit={handleCreateLead} className="space-y-4" data-testid="create-lead-form">
          <FormSection title="Contact" icon={User}>
            <div className="form-grid">
              <div className="space-y-1"><Label className="text-xs">First Name *</Label><Input value={newLead.first_name} onChange={(e) => setNewLead({...newLead, first_name: e.target.value})} required /></div>
              <div className="space-y-1"><Label className="text-xs">Last Name *</Label><Input value={newLead.last_name} onChange={(e) => setNewLead({...newLead, last_name: e.target.value})} required /></div>
              <div className="space-y-1"><Label className="text-xs">Email</Label><Input type="email" value={newLead.email} onChange={(e) => setNewLead({...newLead, email: e.target.value})} /></div>
              <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={newLead.phone} onChange={(e) => setNewLead({...newLead, phone: e.target.value})} /></div>
              <div className="space-y-1"><Label className="text-xs">Guardian Name</Label><Input value={newLead.guardian_name} onChange={(e) => setNewLead({...newLead, guardian_name: e.target.value})} /></div>
            </div>
          </FormSection>

          <FormSection title="Student profile" icon={Student}>
            <div className="form-grid">
              <div className="field">
                <Label className="text-xs">Student Level</Label>
                <select className="form-select" value={newLead.student_level} onChange={(e) => setNewLead({...newLead, student_level: e.target.value})} data-testid="lead-student-level">
                  <option value="">Select...</option>
                  <option value="school">School</option><option value="college">College</option>
                  <option value="competitive">Competitive</option><option value="professional">Professional</option>
                </select>
              </div>
              <div className="field">
                <Label className="text-xs">Source *</Label>
                <select className="form-select" value={newLead.source} onChange={(e) => setNewLead({...newLead, source: e.target.value})} data-testid="lead-source">
                  {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            {newLead.student_level === 'school' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 border rounded bg-muted/30 mt-3">
                <select className="form-select-inline" value={newLead.school_class} onChange={(e) => setNewLead({...newLead, school_class: e.target.value})}>
                  <option value="">Class</option>{Array.from({length:12},(_,i)=>i+1).map(n=><option key={n} value={String(n)}>Class {n}</option>)}
                </select>
                <select className="form-select-inline" value={newLead.school_board} onChange={(e) => setNewLead({...newLead, school_board: e.target.value})}>
                  <option value="">Board</option><option value="CBSE">CBSE</option><option value="SSC">SSC</option><option value="IGSE">IGSE</option><option value="ICSE">ICSE</option><option value="Others">Others</option>
                </select>
                <Input placeholder="School Name" value={newLead.school_name} onChange={(e) => setNewLead({...newLead, school_name: e.target.value})} />
              </div>
            )}
            {newLead.student_level === 'college' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 border rounded bg-muted/30 mt-3">
                <select className="form-select-inline" value={newLead.college_year} onChange={(e) => setNewLead({...newLead, college_year: e.target.value})}>
                  <option value="">Year</option><option value="Inter 1st Year">Inter 1st Year</option><option value="Inter 2nd Year">Inter 2nd Year</option>
                </select>
                <Input placeholder="College Name" value={newLead.college_name} onChange={(e) => setNewLead({...newLead, college_name: e.target.value})} />
              </div>
            )}
            {newLead.student_level === 'competitive' && (
              <div className="p-2 border rounded bg-muted/30 mt-3">
                <select className="form-select" value={newLead.competitive_exam} onChange={(e) => setNewLead({...newLead, competitive_exam: e.target.value})}>
                  <option value="">Exam</option><option value="EAMCET">EAMCET</option><option value="MAINS">MAINS</option><option value="ADVANCED">ADVANCED</option><option value="NEET">NEET</option>
                </select>
              </div>
            )}
            {newLead.student_level === 'professional' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 border rounded bg-muted/30 mt-3">
                <select className="form-select-inline" value={newLead.professional_course} onChange={(e) => setNewLead({...newLead, professional_course: e.target.value})}>
                  <option value="">Course</option><option value="CA">CA</option><option value="CMA">CMA</option><option value="CS">CS</option><option value="US CMA">US CMA</option><option value="Others">Others</option>
                </select>
                <select className="form-select-inline" value={newLead.professional_level} onChange={(e) => setNewLead({...newLead, professional_level: e.target.value})}>
                  <option value="">Level</option><option value="Foundation">Foundation</option><option value="Inter">Inter</option><option value="Final">Final</option><option value="Part 1">Part 1</option><option value="Part 2">Part 2</option>
                </select>
              </div>
            )}
          </FormSection>

          <FormSection title="Interested courses" icon={Books} description="Optional — courses this lead has shown interest in.">
            <div className="max-h-48 overflow-y-auto border rounded p-2 space-y-1" data-testid="lead-interested-courses">
              {courses.map(c => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 p-1 rounded">
                  <input type="checkbox" checked={newLead.interested_courses.includes(c.id)} onChange={(e) => toggleCourse(c.id, e.target.checked)} />
                  {c.title}
                </label>
              ))}
              {courses.length === 0 && <p className="field-hint">No courses available</p>}
            </div>
          </FormSection>

          <FormSection title="Pipeline" icon={Funnel}>
            <div className="space-y-3">
              {staffMembers.length > 0 && (
                <div className="field">
                  <Label className="text-xs">Assign To</Label>
                  <select className="form-select" value={newLead.assigned_to} onChange={(e) => setNewLead({...newLead, assigned_to: e.target.value})} data-testid="lead-assigned-to">
                    <option value="">Unassigned</option>
                    {staffMembers.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                  </select>
                </div>
              )}
              <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea value={newLead.notes} onChange={(e) => setNewLead({...newLead, notes: e.target.value})} rows={3} /></div>
            </div>
          </FormSection>

          {/* Hidden submit so Enter in a field still submits; the toolbar "Create lead" calls requestSubmit() */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
      </RecordPage>
    </DashboardLayout>
  );
}
